/**
 * AffiliateAppearanceTab — the two pictures the public affiliated-ministries
 * pages wear: the directory hero and the banner on each affiliate's profile.
 * Files come off the device; there is no URL to paste.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ExternalLink, RotateCcw, Loader2 } from 'lucide-react';

import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { MediaUpload } from '@/components/MediaUpload';

const KEYS = [
  {
    key: 'affiliates',
    name: 'Directory hero',
    where: 'Top of /affiliated-ministries',
    link: '/affiliated-ministries',
    words: true,
  },
  {
    key: 'affiliate_profile',
    name: 'Profile page banner',
    where: 'Behind the name on every affiliate profile',
    link: '/affiliated-ministries',
    words: false,
  },
];

const HeroCard = ({ meta, hero, onSave, onReset, saving }) => {
  const [draft, setDraft] = React.useState(hero);
  React.useEffect(() => setDraft(hero), [hero]);
  const dirty = ['image_url', 'eyebrow', 'title', 'accent', 'blurb']
    .some((f) => (draft[f] || '') !== (hero[f] || ''));

  return (
    <Card data-testid={`affiliate-hero-card-${meta.key}`}>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base">{meta.name}</CardTitle>
          <CardDescription>{meta.where}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild data-testid={`affiliate-hero-view-${meta.key}`}>
            <a href={meta.link} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> View page
            </a>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onReset(meta.key)}
                  data-testid={`affiliate-hero-reset-${meta.key}`}>
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <MediaUpload
          value={draft.image_url || ''}
          onChange={(url) => setDraft({ ...draft, image_url: url })}
          purpose="hero"
          label="Hero image"
          hint="Landscape works best — around 2000 × 1000. Pick it from your phone or computer."
          testId={`affiliate-hero-image-${meta.key}`}
          previewClassName="h-28"
        />

        {meta.words && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Eyebrow</Label>
              <Input value={draft.eyebrow || ''} data-testid={`affiliate-hero-eyebrow-${meta.key}`}
                     onChange={(e) => setDraft({ ...draft, eyebrow: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Headline</Label>
              <Input value={draft.title || ''} data-testid={`affiliate-hero-title-${meta.key}`}
                     onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Gold phrase</Label>
              <Input value={draft.accent || ''} data-testid={`affiliate-hero-accent-${meta.key}`}
                     onChange={(e) => setDraft({ ...draft, accent: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Blurb</Label>
              <Input value={draft.blurb || ''} data-testid={`affiliate-hero-blurb-${meta.key}`}
                     onChange={(e) => setDraft({ ...draft, blurb: e.target.value })} />
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button size="sm" disabled={!dirty || saving}
                  onClick={() => onSave(meta.key, draft)}
                  data-testid={`affiliate-hero-save-${meta.key}`}>
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Save
          </Button>
          {hero.customised && (
            <span className="text-xs text-slate-500" data-testid={`affiliate-hero-custom-${meta.key}`}>
              Customised{hero.updated_by ? ` by ${hero.updated_by}` : ''}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const AffiliateAppearanceTab = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['affiliate-heroes'],
    queryFn: async () => (await api.get('/admin/public-heroes')).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['affiliate-heroes'] });
    qc.invalidateQueries({ queryKey: ['public-heroes'] });
  };

  const save = useMutation({
    mutationFn: ({ key, body }) => api.put(`/admin/public-heroes/${key}`, body),
    onSuccess: () => { toast.success('Saved — the public page shows it now'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reset = useMutation({
    mutationFn: (key) => api.post(`/admin/public-heroes/${key}/reset`),
    onSuccess: () => { toast.success('Back to the shipped picture'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const heroes = data?.heroes || [];

  return (
    <div className="space-y-4" data-testid="affiliate-appearance-tab">
      {KEYS.map((meta) => {
        const hero = heroes.find((h) => h.key === meta.key) || {};
        return (
          <HeroCard key={meta.key} meta={meta} hero={hero}
                    saving={save.isPending && save.variables?.key === meta.key}
                    onSave={(key, draft) => save.mutate({
                      key,
                      body: {
                        image_url: draft.image_url || '',
                        eyebrow: draft.eyebrow || '',
                        title: draft.title || '',
                        accent: draft.accent || '',
                        blurb: draft.blurb || '',
                      },
                    })}
                    onReset={(key) => reset.mutate(key)} />
        );
      })}
    </div>
  );
};

export default AffiliateAppearanceTab;
